/**
 * Цели роста — экран этапа S11, §10.13 ТЗ.
 *
 * Одна-две цели, не десять: предел держит сервер, экран показывает, сколько
 * ещё можно. Предложения — только те, что посчитал сервер по отставанию от
 * среднего по должности; своей цели «на глаз» здесь поставить нельзя: она
 * стала бы числом, о котором с человеком будут разговаривать месяц.
 *
 * Итог замера пишет сервер, и слова «провалена» в нём нет (§10.13): экран
 * этот текст не пересказывает.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { GoalView } from '../api';

const KEY = ['study', 'mentor', 'goals'];

function show(value: string | null, unit: string): string {
  if (value === null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (unit === '₽') return `${Math.round(numeric).toLocaleString('ru-RU')} ₽`;
  if (unit === '%') return `${numeric.toFixed(1)} %`;
  return `${numeric.toFixed(1)} ${unit}`.trim();
}

function ActiveGoal({ goal, onCancel }: { goal: GoalView; onCancel: () => void }) {
  return (
    <Card title={goal.title}>
      <p>
        Было <b>{show(goal.baseline, goal.unit)}</b>, цель <b>{show(goal.target, goal.unit)}</b> за{' '}
        {goal.shifts_planned} смен.
      </p>
      <p className="faint">
        Замер агент сделает сам, когда пройдут смены, и пришлёт итог в чат — достигнута цель или
        нет.
      </p>
      <Button small variant="quiet" onClick={onCancel}>
        Снять цель
      </Button>
    </Card>
  );
}

export function Goals() {
  const queryClient = useQueryClient();
  const home = useQuery({ queryKey: KEY, queryFn: studyApi.goalsHome });
  const [problem, setProblem] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: KEY });

  const setGoal = useMutation({
    mutationFn: (code: string) => studyApi.setGoal(code),
    onMutate: () => setProblem(null),
    onSuccess: refresh,
    onError: (error) => setProblem(error instanceof Error ? error.message : 'Не получилось.'),
  });
  const cancel = useMutation({
    mutationFn: (id: number) => studyApi.cancelGoal(id),
    onSuccess: refresh,
  });

  if (home.isLoading) return <Loading />;
  if (home.error || !home.data) return <ErrorState error={home.error} onRetry={() => home.refetch()} />;
  const data = home.data;

  if (!data.available) return <Empty title="Цели недоступны" text={data.reason} />;

  const canAdd = data.active.length < data.max_active;

  return (
    <div className="goals">
      {data.active.map((goal) => (
        <ActiveGoal key={goal.id} goal={goal} onCancel={() => cancel.mutate(goal.id)} />
      ))}

      {problem && <p className="answer failure">{problem}</p>}

      {data.suggestions.length > 0 && canAdd && (
        <Card title="Что можно улучшить">
          <p className="faint">
            Зоны, где вы дальше всего от среднего по должности. Цель — половина пути до среднего:
            достижимо и заметно.
          </p>
          <ul className="roleplay-scenarios">
            {data.suggestions.map((item) => (
              <li key={item.metric_code}>
                <p>
                  <b>{item.title}</b>: у вас {show(item.current, item.unit)}, среднее{' '}
                  {show(item.team_avg, item.unit)}.
                </p>
                <Button
                  small
                  variant="primary"
                  disabled={setGoal.isPending}
                  onClick={() => setGoal.mutate(item.metric_code)}
                >
                  Поставить цель {show(item.target, item.unit)}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {!canAdd && (
        <p className="faint">
          Целей уже {data.active.length} — это предел: над двумя одновременно работать можно, над
          большим числом не получается ни над одной.
        </p>
      )}

      {data.active.length === 0 && data.suggestions.length === 0 && (
        <Empty title="Целей пока нет" text={data.reason || 'Отставаний от среднего не видно.'} />
      )}

      {data.history.length > 0 && (
        <Card title="Прошлые цели">
          <ul className="roleplay-history">
            {data.history.map((goal) => (
              <li key={goal.id}>
                <span>{goal.title}</span>
                <span className="faint">
                  {goal.status === 'cancelled' ? 'снята' : (goal.result_text ?? 'без замера')}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
